<template>
  <div :class="['help-page', `help-page--${currentRole}`]">
    <AppSectionHero
      :title="pageContent.title"
      :subtitle="pageContent.subtitle"
      :show-refresh="false"
      :meta-label="pageContent.metaHeading"
      :meta-value="pageContent.metaLabel"
    />

    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="highlight-grid">
          <div class="highlight-card" v-for="item in pageContent.highlights" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </div>
        </div>

        <div class="content-grid">
          <div class="panel-card">
            <h3>{{ pageContent.sectionTitles.steps }}</h3>
            <div class="step-list">
              <div class="step-row" v-for="(item, index) in pageContent.workflow" :key="item">
                <span class="step-number">{{ index + 1 }}</span>
                <div class="step-copy">{{ item }}</div>
              </div>
            </div>
          </div>

          <div class="panel-card">
            <h3>{{ pageContent.sectionTitles.links }}</h3>
            <div class="resource-list">
              <router-link
                v-for="item in pageContent.resources"
                :key="item.title"
                :to="item.to"
                class="resource-card resource-card--link"
              >
                <div class="resource-title">{{ item.title }}</div>
                <div class="resource-copy">{{ item.copy }}</div>
              </router-link>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <h3>{{ pageContent.sectionTitles.faq }}</h3>
          <div class="faq-list">
            <div class="faq-item" v-for="item in pageContent.faq" :key="item.question">
              <div class="faq-question">{{ item.question }}</div>
              <div class="faq-answer">{{ item.answer }}</div>
            </div>
          </div>
        </div>

        <div class="panel-card quick-contact-card">
          <h3>{{ pageContent.sectionTitles.contact }}</h3>
          <div class="quick-contact">
            <img :src="quickContactImage" :alt="pageContent.quickContact.name" class="quick-contact__avatar">
            <div class="quick-contact__name">{{ pageContent.quickContact.name }}</div>
            <div class="quick-contact__role">{{ pageContent.quickContact.role }}</div>
            <div class="quick-contact__detail">
              <span class="quick-contact__label">{{ pageContent.quickContact.phoneLabel }}</span>
              <span>+66 (0) 5391 6026</span>
            </div>
            <div class="quick-contact__detail">
              <span class="quick-contact__label">{{ pageContent.quickContact.emailLabel }}</span>
              <a href="mailto:sivatid.pan@mfu.ac.th" class="quick-contact__link">sivatid.pan@mfu.ac.th</a>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import quickContactImage from '@/assets/admin.jpeg'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { detectRouteRole } from '@/utils/role-dashboard'

const CONTENT_BY_LOCALE = {
  en: {
    committee: {
      title: 'Help & Support',
      subtitle: 'Find support for assigned reviews, comment handling, and committee completion steps.',
      metaHeading: 'Support Scope',
      metaLabel: 'Committee Support',
      sectionTitles: { steps: 'Support Steps', links: 'Quick Links', faq: 'Frequently Asked Questions', contact: 'Quick Contact' },
      quickContact: { name: 'Mr Sivatid Pankaew', role: 'International Collaboration', phoneLabel: 'Tel:', emailLabel: 'Email:' },
      highlights: [
        { title: 'Review help', copy: 'Use support when an assigned agreement is missing, unreadable, or blocked by workflow issues.' },
        { title: 'Comment traceability', copy: 'Ask for help if the comment thread, article references, or preview content are out of sync.' },
        { title: 'Final handoff', copy: 'Get assistance before sending a completed committee review to admin if the workflow stage looks wrong.' }
      ],
      workflow: [
        'Check the committee dashboard to confirm the agreement is assigned to you.',
        'Open the review page and verify the draft preview, comment thread, and workflow stage.',
        'Use the guideline page if you need process reminders while reviewing.',
        'If something is wrong, capture the agreement title and current stage before reporting the issue.',
        'After support resolves the issue, return to the same review and continue the committee workflow.'
      ],
      resources: [
        { title: 'Committee Dashboard', copy: 'Open current assignments and continue active reviews.', to: '/dashboard/committee' },
        { title: 'Review History', copy: 'Check previous committee outcomes and saved comments.', to: { path: '/review-history', query: { workspace: 'committee' } } },
        { title: 'Guideline', copy: 'Review committee guidance, handoff rules, and comment practices.', to: { path: '/guideline', query: { workspace: 'committee' } } }
      ],
      faq: [
        { question: 'What if the draft preview is missing?', answer: 'Check whether the agreement was submitted with an uploaded PDF or builder draft. If the preview panel is still blank, report the agreement to support with the title and current review stage.' },
        { question: 'What if I cannot submit the committee review?', answer: 'Confirm the agreement is still assigned to you and that the workflow has not already moved to admin. If the page still blocks submission, contact support.' },
        { question: 'What if comments do not update?', answer: 'Refresh the page, reopen the assignment from the committee dashboard, and verify the agreement is still in committee review. If it remains stale, report it as a sync issue.' }
      ]
    },
    admin: {
      title: 'Help & Support',
      subtitle: 'Find support for admin assignment, final approval, and agreement workflow issues.',
      metaHeading: 'Support Scope',
      metaLabel: 'Admin Support',
      sectionTitles: { steps: 'Support Steps', links: 'Quick Links', faq: 'Frequently Asked Questions', contact: 'Quick Contact' },
      quickContact: { name: 'Mr Sivatid Pankaew', role: 'International Collaboration', phoneLabel: 'Tel:', emailLabel: 'Email:' },
      highlights: [
        { title: 'Assignment help', copy: 'Use support when committee assignees are missing or the review queue does not match the agreement state.' },
        { title: 'Workflow checks', copy: 'Get help if an agreement is stuck between user, committee, and admin stages.' },
        { title: 'Access issues', copy: 'Use support when permissions, notifications, or dashboard data do not match the expected role.' }
      ],
      workflow: [
        'Confirm the agreement status and current workflow step before reporting the issue.',
        'Check whether the problem is in assignment, committee review, or final decision stage.',
        'Use the guideline page if you need process clarification first.',
        'Capture the agreement title, current stage, and visible error or mismatch.',
        'Return to the relevant admin page after the issue is resolved and continue the workflow.'
      ],
      resources: [
        { title: 'Review Queue', copy: 'Open agreements waiting for assignment or final admin action.', to: '/review-queue' },
        { title: 'Reports & Analytics', copy: 'Check agreement counts, geography, and workflow movement.', to: '/reports' },
        { title: 'Guideline', copy: 'Review admin workflow guidance and final approval rules.', to: '/guideline' }
      ],
      faq: [
        { question: 'What if committee users do not appear for assignment?', answer: 'Verify that the account has an active committee-related security group in the system. If the user should appear but still does not, report it as an assignment source issue.' },
        { question: 'What if the user status does not change after final approval?', answer: 'Refresh the agreement detail and user queue once. If the status still shows the old stage, report it as a workflow sync issue.' },
        { question: 'What if reports or sidebar counts look wrong?', answer: 'Refresh the page first, then compare the queue totals against the dashboard. If they still do not match, report the mismatch with the role and page name.' }
      ]
    }
  },
  th: {
    committee: {
      title: 'ช่วยเหลือและสนับสนุน',
      subtitle: 'ค้นหาความช่วยเหลือสำหรับงานรีวิวที่ได้รับมอบหมาย การจัดการความคิดเห็น และขั้นตอนปิดงานของคณะกรรมการ',
      metaHeading: 'ขอบเขตการสนับสนุน',
      metaLabel: 'การสนับสนุนคณะกรรมการ',
      sectionTitles: { steps: 'ขั้นตอนการขอความช่วยเหลือ', links: 'ลิงก์ด่วน', faq: 'คำถามที่พบบ่อย', contact: 'ติดต่อด่วน' },
      quickContact: { name: 'คุณศิวธิดา ปัญญาแก้ว', role: 'ความร่วมมือระหว่างประเทศ', phoneLabel: 'โทร:', emailLabel: 'อีเมล:' },
      highlights: [
        { title: 'ช่วยเหลืองานรีวิว', copy: 'ขอความช่วยเหลือเมื่อไม่พบข้อตกลงที่ได้รับมอบหมาย ไฟล์อ่านไม่ได้ หรือเวิร์กโฟลว์มีปัญหา.' },
        { title: 'ติดตามความเชื่อมโยงของความเห็น', copy: 'แจ้งขอความช่วยเหลือเมื่อเธรดความคิดเห็น การอ้างอิงมาตรา หรือตัวอย่างเอกสารไม่สอดคล้องกัน.' },
        { title: 'ส่งต่องานขั้นสุดท้าย', copy: 'ขอความช่วยเหลือก่อนส่งผลรีวิวของคณะกรรมการกลับไปยังผู้ดูแลระบบ หากขั้นตอนเวิร์กโฟลว์ดูไม่ถูกต้อง.' }
      ],
      workflow: [
        'ตรวจสอบในแดชบอร์ดคณะกรรมการว่าข้อตกลงถูกมอบหมายให้คุณแล้ว.',
        'เปิดหน้ารีวิวและตรวจสอบตัวอย่างเอกสาร เธรดความคิดเห็น และสถานะเวิร์กโฟลว์.',
        'ใช้หน้าคู่มือหากต้องการทบทวนขั้นตอนระหว่างการรีวิว.',
        'หากพบปัญหา ให้บันทึกชื่อข้อตกลงและขั้นตอนปัจจุบันก่อนรายงานปัญหา.',
        'เมื่อทีมสนับสนุนแก้ไขแล้ว ให้กลับไปยังงานรีวิวเดิมและดำเนินการต่อ.'
      ],
      resources: [
        { title: 'แดชบอร์ดคณะกรรมการ', copy: 'เปิดงานที่ได้รับมอบหมายและทำงานรีวิวที่กำลังดำเนินการต่อ.', to: '/dashboard/committee' },
        { title: 'ประวัติการรีวิว', copy: 'ตรวจสอบผลการตัดสินใจและความคิดเห็นที่บันทึกไว้ก่อนหน้า.', to: { path: '/review-history', query: { workspace: 'committee' } } },
        { title: 'คู่มือ', copy: 'ทบทวนแนวทางการรีวิว กฎการส่งต่องาน และวิธีจัดการความคิดเห็น.', to: { path: '/guideline', query: { workspace: 'committee' } } }
      ],
      faq: [
        { question: 'ถ้าตัวอย่างเอกสารไม่แสดงต้องทำอย่างไร?', answer: 'ตรวจสอบก่อนว่าข้อตกลงถูกส่งมาด้วย PDF หรือร่างแบบ builder หากแผงตัวอย่างยังว่างอยู่ ให้รายงานชื่อข้อตกลงและขั้นตอนรีวิวปัจจุบันให้ทีมสนับสนุน.' },
        { question: 'ถ้าฉันส่งผลรีวิวของคณะกรรมการไม่ได้ต้องทำอย่างไร?', answer: 'ตรวจสอบว่าข้อตกลงยังถูกมอบหมายให้คุณอยู่ และเวิร์กโฟลว์ยังไม่ย้ายไปยังผู้ดูแลระบบแล้ว หากหน้ายังบล็อกการส่ง ให้ติดต่อทีมสนับสนุน.' },
        { question: 'ถ้าความคิดเห็นไม่อัปเดตต้องทำอย่างไร?', answer: 'รีเฟรชหน้า เปิดงานจากแดชบอร์ดคณะกรรมการอีกครั้ง และตรวจสอบว่าข้อตกลงยังอยู่ในขั้นคณะกรรมการ หากยังไม่อัปเดต ให้รายงานเป็นปัญหาการซิงก์.' }
      ]
    },
    admin: {
      title: 'ช่วยเหลือและสนับสนุน',
      subtitle: 'ค้นหาความช่วยเหลือสำหรับการมอบหมายงาน การอนุมัติขั้นสุดท้าย และปัญหาเวิร์กโฟลว์ของข้อตกลง',
      metaHeading: 'ขอบเขตการสนับสนุน',
      metaLabel: 'การสนับสนุนผู้ดูแลระบบ',
      sectionTitles: { steps: 'ขั้นตอนการขอความช่วยเหลือ', links: 'ลิงก์ด่วน', faq: 'คำถามที่พบบ่อย', contact: 'ติดต่อด่วน' },
      quickContact: { name: 'คุณศิวธิดา ปัญญาแก้ว', role: 'ความร่วมมือระหว่างประเทศ', phoneLabel: 'โทร:', emailLabel: 'อีเมล:' },
      highlights: [
        { title: 'ช่วยเหลือด้านการมอบหมาย', copy: 'ขอความช่วยเหลือเมื่อไม่พบผู้ตรวจคณะกรรมการ หรือคิวรีวิวไม่ตรงกับสถานะข้อตกลง.' },
        { title: 'ตรวจสอบเวิร์กโฟลว์', copy: 'ขอความช่วยเหลือหากข้อตกลงค้างอยู่ระหว่างขั้นผู้ใช้ คณะกรรมการ และผู้ดูแลระบบ.' },
        { title: 'ปัญหาการเข้าถึง', copy: 'ขอความช่วยเหลือเมื่อสิทธิ์ การแจ้งเตือน หรือข้อมูลบนแดชบอร์ดไม่ตรงกับบทบาทที่ควรเป็น.' }
      ],
      workflow: [
        'ยืนยันสถานะข้อตกลงและขั้นตอนเวิร์กโฟลว์ปัจจุบันก่อนรายงานปัญหา.',
        'ตรวจสอบว่าปัญหาอยู่ในขั้นมอบหมาย รีวิวของคณะกรรมการ หรือการตัดสินใจขั้นสุดท้าย.',
        'ใช้หน้าคู่มือหากต้องการทบทวนขั้นตอนก่อน.',
        'บันทึกชื่อข้อตกลง ขั้นตอนปัจจุบัน และข้อผิดพลาดหรือความไม่ตรงกันที่เห็นบนหน้าจอ.',
        'กลับไปยังหน้าผู้ดูแลระบบที่เกี่ยวข้องหลังจากปัญหาได้รับการแก้ไขแล้ว และดำเนินงานต่อ.'
      ],
      resources: [
        { title: 'คิวรีวิว', copy: 'เปิดข้อตกลงที่รอการมอบหมายหรือการดำเนินการขั้นสุดท้ายจากผู้ดูแลระบบ.', to: '/review-queue' },
        { title: 'รายงานและการวิเคราะห์', copy: 'ตรวจสอบจำนวนข้อตกลง พื้นที่ทางภูมิศาสตร์ และความเคลื่อนไหวของเวิร์กโฟลว์.', to: '/reports' },
        { title: 'คู่มือ', copy: 'ทบทวนแนวทางของผู้ดูแลระบบและกฎการอนุมัติขั้นสุดท้าย.', to: '/guideline' }
      ],
      faq: [
        { question: 'ถ้าผู้ใช้คณะกรรมการไม่แสดงในหน้ามอบหมายต้องทำอย่างไร?', answer: 'ตรวจสอบว่าบัญชีนั้นมี security group ที่เกี่ยวข้องกับคณะกรรมการและยัง active อยู่ หากผู้ใช้ควรแสดงแต่ยังไม่ขึ้น ให้รายงานเป็นปัญหาแหล่งข้อมูลการมอบหมาย.' },
        { question: 'ถ้าสถานะผู้ใช้ไม่เปลี่ยนหลังอนุมัติขั้นสุดท้ายต้องทำอย่างไร?', answer: 'รีเฟรชหน้ารายละเอียดข้อตกลงและคิวของผู้ใช้หนึ่งครั้ง หากสถานะยังเป็นขั้นเดิม ให้รายงานเป็นปัญหาการซิงก์เวิร์กโฟลว์.' },
        { question: 'ถ้ารายงานหรือจำนวนในแถบด้านข้างดูไม่ถูกต้องต้องทำอย่างไร?', answer: 'รีเฟรชหน้าก่อน จากนั้นเปรียบเทียบยอดในคิวกับแดชบอร์ด หากยังไม่ตรงกัน ให้รายงานความคลาดเคลื่อนพร้อมระบุบทบาทและชื่อหน้า.' }
      ]
    }
  }
}

export default {
  name: 'HelpPage',
  components: { AppSectionHero },
  data() {
    return {
      quickContactImage
    }
  },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    currentRole() {
      return detectRouteRole(this.$store, this.$route)
    },
    pageContent() {
      return CONTENT_BY_LOCALE[this.localeCode][this.currentRole === 'committee' ? 'committee' : 'admin']
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("help-page");
@include workspace-info-layout;

.resource-card--link { display: block; text-decoration: none; }
.resource-card--link:hover { border-color: #d7dde6; text-decoration: none; }

.help-page--committee .shell {
  background:
    radial-gradient(circle at top right, rgba(140, 21, 21, 0.1), transparent 20%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 249, 248, 0.99) 100%);
}

.help-page--committee .highlight-card::before {
  background: linear-gradient(90deg, #8c1515, #c2410c);
}

.help-page--committee .step-number {
  background: linear-gradient(135deg, #8c1515 0%, #b42318 100%);
  box-shadow: 0 10px 18px rgba(140, 21, 21, 0.2);
}

.help-page--committee .panel-card h3,
.help-page--committee .highlight-card h3,
.help-page--committee .resource-title,
.help-page--committee .faq-question {
  color: #7f1d1d;
}

.help-page--committee .resource-card {
  background: linear-gradient(180deg, #fff8f7 0%, #ffffff 100%);
  border-color: #efd4d1;
}

.help-page--committee .resource-card--link:hover {
  border-color: #d7a39d;
  box-shadow: 0 18px 34px rgba(140, 21, 21, 0.12);
}

.help-page--committee .faq-item {
  background: linear-gradient(180deg, #fff9f8 0%, #ffffff 100%);
  border-color: #f1ddda;
}

.quick-contact-card {
  margin-top: 18px;
}

.quick-contact {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0 4px;
  text-align: center;
}

.quick-contact__avatar {
  background: #fff;
  border: 6px solid #f4f7fb;
  border-radius: 999px;
  box-shadow: 0 14px 28px rgba(140, 21, 21, 0.16);
  height: 140px;
  object-fit: cover;
  width: 140px;
}

.quick-contact__name {
  color: #223045;
  font-size: 1.55rem;
  font-weight: 800;
  margin-top: 6px;
}

.quick-contact__role {
  color: #7f1d1d;
  font-size: 1.15rem;
  font-weight: 700;
}

.quick-contact__detail {
  color: #66758a;
  font-size: 1rem;
  line-height: 1.6;
}

.quick-contact__label {
  color: #52606d;
  font-weight: 800;
  margin-right: 8px;
}

.quick-contact__link {
  color: #b42318;
  font-weight: 800;
  text-decoration: underline;
}
</style>
